import React from 'react'
import { Card, CardMedia, Typography, Button, Box } from '@mui/material'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { makeStyles } from 'tss-react/mui'
import { currencyFormatter } from 'src/Libs/shop/utils/formatter'
import { Product } from 'src/Libs/shop/models/system-configuration-components.model'
import { ShopImageThumnbailSizes } from 'src/Libs/shop/types'
import { getImageUrlBySize } from 'src/Libs/shop'
import { QuantityAvailabilityIndicator } from '../../../ShopProduct'

interface QntmProductCardProps {
  productData: Product
  onMoreInfo: (productId: string) => void
}

const useStyles = makeStyles()(theme => ({
  product: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    minWidth: '265px',
    maxWidth: '225px',
    flexShrink: 0,
    margin: '12px',
    padding: 0,
    paddingBottom: '12px',
    position: 'relative',
    border: `1.5px solid ${theme.palette.primary.main}`,
    boxShadow: 'none',
  },
  activeLabel: {
    top: theme.spacing(2),
    left: theme.spacing(2),
    position: 'absolute',
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
    borderRadius: '24px',
  },
  productImage: {
    width: '100%',
    marginBottom: '12px',
    '&:hover': {
      cursor: 'pointer',
    },
  },
  buttonContainer: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
    display: 'flex',
    gap: theme.spacing(2),
    paddingLeft: '8px',
    paddingRight: '8px',
  },
  button: {
    fontSize: '16px',
    padding: '16px 0px',
  },
  containerTextWrapper: {
    paddingLeft: '8px',
    paddingRight: '8px',
  },
  flexPrice: {
    display: 'flex',
    alignItems: 'flex-end',
  },
}))

const QntmProductCard: React.FC<QntmProductCardProps> = ({ productData, onMoreInfo }) => {
  const { classes } = useStyles()
  const { t } = useCustomTranslation()

  const unitPrice = productData.calculatedPrice.unitPrice
  const price = productData.calculatedPrice.listPrice?.price ?? 0
  const productImageUrl = getImageUrlBySize(productData?.cover?.media?.thumbnails, ShopImageThumnbailSizes.Medium)
  const productAvailability = productData.extensions?.e3dcAvailability

  return (
    <Card className={classes.product}>
      <Typography className={classes.activeLabel}>{t('shop.common.selected')}</Typography>

      <Box>
        <CardMedia
          className={classes.productImage}
          component="img"
          image={productImageUrl}
          alt="Product"
          onClick={() => onMoreInfo(productData.id)}
        />

        <Box className={classes.containerTextWrapper}>
          <Typography variant="h3">
            {productData?.translated?.name}
            <QuantityAvailabilityIndicator quantityAvailableType={productAvailability} />
          </Typography>
        </Box>
      </Box>

      <Box>
        <Box className={classes.containerTextWrapper}>
          <Box className={classes.flexPrice}>
            <Typography variant="h5">{unitPrice ? currencyFormatter(unitPrice) : currencyFormatter(price)}</Typography>

            {unitPrice < price && (
              <Typography ml={2} sx={{ textDecorationLine: 'line-through' }} variant="h5">
                {currencyFormatter(price)}
              </Typography>
            )}
          </Box>
        </Box>

        <Box className={classes.buttonContainer}>
          <Button className={classes.button} size="large" color="inherit" variant="outlined" fullWidth onClick={() => onMoreInfo(productData.id)}>
            {t('shop.common.more-info')}
          </Button>

          <Button className={classes.button} variant="contained" fullWidth disabled>
            {t('shop.common.selected')}
          </Button>
        </Box>
      </Box>
    </Card>
  )
}

export default QntmProductCard
